import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PinIcon from '@mui/icons-material/Pin';
import PreviewIcon from '@mui/icons-material/Preview';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

export type MoreMenuAction = {
  label: string;
  icon: ReactNode;
  onClick: VoidFunction;
  hidden?: boolean;
};

type UserMoreMenuProps = {
  onDelete?: VoidFunction;
  onChangePin?: VoidFunction;
  onScore?: VoidFunction;
  onPreview?: VoidFunction;
  viewResults?: VoidFunction;
  viewStatistics?: VoidFunction;
  duplicate?: VoidFunction;
  id?: string;
  groups?: boolean;
  hasPin?: boolean;
  isNotification?: boolean;
  isNotificationDelivered?: boolean;
  hideEdit?: boolean;
  editUrl?: string;
  customActions?: MoreMenuAction[];
};

export default function MoreMenu({
  onDelete,
  onChangePin,
  onScore,
  onPreview,
  viewResults,
  viewStatistics,
  duplicate,
  id,
  groups,
  hasPin,
  isNotification,
  isNotificationDelivered,
  hideEdit,
  editUrl,
  customActions,
}: UserMoreMenuProps) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const visibleCustomActions = (customActions ?? []).filter(
    (action) => !action.hidden,
  );

  const handleActionClick = (action: VoidFunction) => {
    setIsOpen(false);
    action();
  };

  return (
    <>
      <IconButton ref={ref} onClick={() => setIsOpen(true)}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        slotProps={{
          paper: {
            sx: { width: 200, maxWidth: '100%' },
          },
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!hideEdit && (
          <MenuItem
            component={RouterLink}
            to={editUrl ?? `${id}`}
            onClick={() => setIsOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              {isNotification && isNotificationDelivered ? (
                <PreviewIcon />
              ) : (
                <EditIcon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                isNotification && isNotificationDelivered
                  ? t('moreMenu.view')
                  : t('moreMenu.edit')
              }
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {visibleCustomActions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => handleActionClick(action.onClick)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText
              primary={action.label}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        ))}
        {onPreview && (
          <MenuItem
            onClick={() => handleActionClick(onPreview)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <PreviewIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.view')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {groups && (
          <MenuItem
            component={RouterLink}
            to={`${id}/groups`}
            onClick={() => setIsOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <ManageAccountsIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.manageGroups')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {hasPin && (
          <MenuItem
            sx={{ color: 'text.secondary' }}
            onClick={() => onChangePin && handleActionClick(onChangePin)}
          >
            <ListItemIcon>
              <PinIcon />
            </ListItemIcon>
            <ListItemText
              primary="PIN"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        {onScore && (
          <MenuItem
            sx={{ color: 'text.secondary' }}
            onClick={() => handleActionClick(onScore)}
          >
            <ListItemIcon>
              <RefreshIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.score')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {viewResults && (
          <MenuItem
            onClick={() => handleActionClick(viewResults)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <ListIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.results')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {viewStatistics && (
          <MenuItem
            onClick={() => handleActionClick(viewStatistics)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <StackedLineChartIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.statistics')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {duplicate && (
          <MenuItem
            onClick={() => handleActionClick(duplicate)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <ContentCopyIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.duplicate')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={() => handleActionClick(onDelete)}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('moreMenu.delete')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
